import React, { useState, useEffect } from 'react';
import { Download, Upload, HardDrive, History, RotateCcw, Trash2, Clock, CheckCircle, Mail, Settings, Eye, EyeOff, Cloud } from 'lucide-react';
import { toast } from '../utils/toast';
import { carregarHistorico, salvarSnapshot, removerSnapshot, proximoDoLimite, slotsRestantes, proximosDeletados } from '../utils/autoBackup';
import { carregarConfigEmail, salvarConfigEmail, enviarBackupPorEmail } from '../utils/emailBackup';
import { supabase } from '../lib/supabase';
import { encryptData } from '../utils/encryption';
import { listarSnapshotsNuvem, baixarSnapshotNuvem, excluirSnapshotNuvem } from '../utils/cloudBackup';

export function Backup({ data, onRestore }) {
  const [mensagem, setMensagem]       = useState('');
  const [historico, setHistorico]     = useState([]);
  const [confirmando, setConfirmando] = useState(null);
  const [configEmail, setConfigEmail] = useState(carregarConfigEmail);
  const [mostraSenha, setMostraSenha] = useState(false);
  const [enviandoTeste, setEnviandoTeste] = useState(false);
  const [historicoNuvem, setHistoricoNuvem] = useState([]);
  const [carregandoNuvem, setCarregandoNuvem] = useState(false);
  const [confirmandoNuvem, setConfirmandoNuvem] = useState(null);

  const recarregarHistorico = () => setHistorico(carregarHistorico());

  const carregarHistoricoNuvem = async () => {
    setCarregandoNuvem(true);
    try {
      const lista = await listarSnapshotsNuvem();
      setHistoricoNuvem(lista);
    } catch {
      toast.erro('Não foi possível carregar o histórico da nuvem.');
    } finally {
      setCarregandoNuvem(false);
    }
  };

  useEffect(() => { carregarHistoricoNuvem(); }, []);

  useEffect(() => { recarregarHistorico(); }, []);

  const salvarEmail = () => {
    salvarConfigEmail(configEmail);
    setMensagem('✅ Configurações de email salvas!');
    setTimeout(() => setMensagem(''), 3000);
  };

  const testarEmail = async () => {
    // Garante snapshot disponível — cria um temporário se não houver
    let ultimo = carregarHistorico()[0];
    if (!ultimo) {
      const { snapshot } = salvarSnapshot(data, 'manual');
      ultimo = snapshot;
      recarregarHistorico();
    }
    setEnviandoTeste(true);
    setMensagem('⏳ Enviando email de teste...');
    const resultado = await enviarBackupPorEmail(ultimo);
    setEnviandoTeste(false);
    if (resultado.ok) {
      setMensagem('✅ Email enviado com sucesso para bianca@kzeroconsultoria.com.br!');
    } else {
      setMensagem(`❌ Erro: ${resultado.erro}`);
    }
    setTimeout(() => setMensagem(''), 8000);
  };

  const totalRegistros = Object.values(data).reduce((s, v) => s + (Array.isArray(v) ? v.length : 0), 0);

  const baixarBackup = () => {
    const { snapshot: snap } = salvarSnapshot(data, 'manual');
    recarregarHistorico();
    const backup = { versao: '2.0', geradoEm: snap.geradoEm, dados: data };
    const blob   = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url    = URL.createObjectURL(blob);
    const link   = document.createElement('a');
    link.href     = url;
    link.download = `Kzero_Backup_${snap.data}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setMensagem('✅ Backup salvo e baixado!');
    setTimeout(() => setMensagem(''), 4000);
  };

  // Restaurar volta itens a uma versão anterior — exatamente o que a trava
  // otimista do banco (mig 022) recusa num upsert comum. Por isso vai pela RPC
  // restaurar_dados: só administrador, com snapshot do estado atual antes.
  // Até 30/09/2026 o erro era engolido e a tela dizia "restaurado" com o banco
  // intacto; agora o erro sobe para quem chamou mostrar.
  const sincronizarNuvem = async (dadosRestaurados) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Você não está logada. Faça login e tente de novo.');
    // O snapshot local guarda os dados já decifrados; cifra antes de gravar
    // (encryptData não cifra de novo o que já está cifrado).
    const { error } = await supabase.rpc('restaurar_dados', { p_dados: encryptData(dadosRestaurados) });
    if (error) throw new Error(error.message || 'erro desconhecido do Supabase');
  };

  // 2026-07-31 Dex: quando o app engole erros de sync silenciosamente,
  // dados só ficam locais. Este botão força upsert do estado atual e
  // REPORTA erro real (não engole). Uso: "meus eventos de hoje sumiram
  // quando abro noutro browser".
  const [sincronizandoForce, setSincronizandoForce] = useState(false);
  const forcarSincronizacao = async () => {
    setSincronizandoForce(true);
    setMensagem('⏳ Enviando dados atuais pra nuvem...');
    try {
      const { data: { session }, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw new Error('Erro de sessão: ' + sessErr.message);
      if (!session) throw new Error('Você não está logada. Faça login e tente de novo.');

      const { error } = await supabase.from('dados_compartilhados').upsert({
        id: 'sistema',
        dados: data,
        atualizado_em: new Date().toISOString(),
        atualizado_por: session.user.id,
      });
      if (error) throw new Error(error.message || 'erro desconhecido do Supabase');

      // Confirma lendo de volta
      const { data: row, error: readErr } = await supabase
        .from('dados_compartilhados')
        .select('atualizado_em')
        .eq('id', 'sistema')
        .single();
      if (readErr) throw new Error('Confirmação falhou: ' + readErr.message);

      const totalAgs = Array.isArray(data?.agendamentos) ? data.agendamentos.length : 0;
      setMensagem(`✅ Sincronizado! ${totalAgs} agendamentos no servidor. Última atualização: ${new Date(row.atualizado_em).toLocaleString('pt-BR')}.`);
    } catch (e) {
      setMensagem('❌ FALHA REAL ao sincronizar: ' + (e?.message || 'erro desconhecido') + ' — copie esta mensagem e envie ao suporte.');
    } finally {
      setSincronizandoForce(false);
      setTimeout(() => setMensagem(''), 20000);
    }
  };

  const restaurarVersaoNuvem = async (row) => {
    try {
      const payload = await baixarSnapshotNuvem(row.path);
      if (!payload?.dados) { toast.erro('Arquivo de backup inválido.'); return; }
      salvarSnapshot(data, 'manual');
      await sincronizarNuvem(payload.dados);
      onRestore(payload.dados);
      recarregarHistorico();
      setConfirmandoNuvem(null);
      toast.sucesso('Dados restaurados da versão salva na nuvem.');
      carregarHistoricoNuvem();
    } catch (e) {
      toast.erro('Falha ao restaurar da nuvem: ' + (e?.message || 'erro desconhecido'));
    }
  };

  const baixarSnapshotNuvemUI = async (row) => {
    try {
      const payload = await baixarSnapshotNuvem(row.path);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kzero_Nuvem_${row.path}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.erro('Falha ao baixar: ' + (e?.message || 'erro'));
    }
  };

  const excluirSnapshotNuvemUI = async (row) => {
    if (!window.confirm('Excluir este backup da nuvem? A ação é irreversível.')) return;
    try {
      await excluirSnapshotNuvem(row.path);
      toast.sucesso('Backup removido da nuvem.');
      carregarHistoricoNuvem();
    } catch (e) {
      toast.erro('Falha ao excluir: ' + (e?.message || 'erro'));
    }
  };

  const restaurarArquivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        if (json.dados) {
          salvarSnapshot(data, 'manual');
          sincronizarNuvem(json.dados)
            .then(() => {
              onRestore(json.dados);
              recarregarHistorico();
              setMensagem('✅ Backup restaurado e sincronizado com a nuvem!');
            })
            .catch((err) => setMensagem('❌ Backup NÃO restaurado: ' + (err?.message || 'erro desconhecido')));
        } else {
          setMensagem('❌ Arquivo inválido.');
        }
      } catch {
        setMensagem('❌ Erro ao ler o arquivo.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const restaurarSnapshot = async (snap) => {
    salvarSnapshot(data, 'manual');
    try {
      await sincronizarNuvem(snap.dados);
    } catch (e) {
      setConfirmando(null);
      toast.erro('Backup NÃO restaurado: ' + (e?.message || 'erro desconhecido'));
      return;
    }
    onRestore(snap.dados);
    recarregarHistorico();
    setConfirmando(null);
    toast.sucesso(`Dados restaurados para ${snap.data} às ${snap.hora} e sincronizados com a nuvem.`);
  };

  const excluirSnapshot = (id) => {
    removerSnapshot(id);
    recarregarHistorico();
  };

  const baixarSnapshot = (snap) => {
    const backup = { versao: '2.0', geradoEm: snap.geradoEm, dados: snap.dados };
    const blob   = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url    = URL.createObjectURL(blob);
    const link   = document.createElement('a');
    link.href     = url;
    link.download = `Kzero_Backup_${snap.data}_${snap.hora.replace(':','h')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const backupHojeFeito = historico.some(h => {
    const hoje = new Date().toISOString().split('T')[0];
    return h.data === hoje && h.tipo === 'automatico';
  });

  const alertaLimite    = proximoDoLimite();
  const slots           = slotsRestantes();
  const aSeremDeletados = proximosDeletados();

  return (
    <div className="backup-module" style={s.container}>
      <h2 style={s.titulo}>🛡 Backup e Restauração</h2>

      {mensagem && (
        <div style={{ ...s.alerta, ...(mensagem.startsWith('✅') ? s.alertaOk : s.alertaErro) }}>
          {mensagem}
        </div>
      )}

      {/* Aviso de limite próximo */}
      {alertaLimite && (
        <div style={s.alertaLimite}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
            <span style={{ fontSize:20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight:700, marginBottom:4 }}>
                {slots <= 0
                  ? 'Limite de backups atingido — próximo backup automático removerá os mais antigos'
                  : `Atenção: restam apenas ${slots} slot(s) de backup (máx. 36)`}
              </div>
              {aSeremDeletados.length > 0 && (
                <div style={{ fontSize:12, color:'#b45309', marginBottom:6 }}>
                  O próximo backup automático removerá {aSeremDeletados.length} backup(s) antigo(s):
                  <ul style={{ margin:'4px 0 0', paddingLeft:16 }}>
                    {aSeremDeletados.map(b => (
                      <li key={b.id}>
                        {b.data.split('-').reverse().join('/')} às {b.hora} · {b.tipo === 'automatico' ? 'Automático' : 'Manual'} · {b.totalRegistros} registros
                        {' '}
                        <button style={{ background:'none', border:'none', color:'#1d4ed8', cursor:'pointer', fontSize:11, textDecoration:'underline', padding:0 }} onClick={() => baixarSnapshot(b)}>
                          Baixar agora ↓
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ fontSize:12, color:'#b45309' }}>
                💡 Baixe os backups antigos antes que sejam removidos, ou exclua manualmente os que não precisar.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status */}
      <div style={s.card}>
        <div style={s.secaoHeader}>
          <HardDrive size={20} color="#10b981"/>
          <div style={s.secaoTxt}>
            <div style={s.secaoTitulo}>Status atual dos dados</div>
            <div style={s.secaoDesc}>
              Backup automático a cada 10 minutos — {historico.length > 0
                ? <span style={{ color:'#10b981' }}>✓ Último às {historico[0]?.hora}</span>
                : <span style={{ color:'#f59e0b' }}>⏳ Aguardando primeiro ciclo</span>}
            </div>
          </div>
        </div>
        <div style={s.statsGrid}>
          {[
            { label: 'Clientes',     valor: data.clientes?.length || 0,     cor: '#f59e0b' },
            { label: 'Agendamentos', valor: data.agendamentos?.length || 0,  cor: '#3b82f6' },
            { label: 'Fluxo',        valor: data.fluxoCaixa?.length || 0,    cor: '#10b981' },
            { label: 'Reembolsos',   valor: data.reembolsos?.length || 0,    cor: '#ef4444' },
            { label: 'Anotações',    valor: data.anotacoes?.length || 0,     cor: '#8b5cf6' },
            { label: 'Atas',         valor: data.atas?.length || 0,          cor: '#06b6d4' },
          ].map(item => (
            <div key={item.label} style={s.statBox}>
              <div style={{ ...s.statNum, color: item.cor }}>{item.valor}</div>
              <div style={s.statLabel}>{item.label}</div>
            </div>
          ))}
        </div>
        <div style={s.totalTxt}>Total: <strong>{totalRegistros} registros</strong> armazenados localmente neste navegador.</div>
      </div>

      {/* Força sincronização — resolve "meus eventos sumiram quando abri em outro browser" */}
      <div style={{ ...s.card, border: '2px solid #f59e0b', marginBottom: '1rem' }}>
        <div style={s.secaoHeader}>
          <Cloud size={20} color="#f59e0b"/>
          <div style={s.secaoTxt}>
            <div style={s.secaoTitulo}>Sincronizar dados com a nuvem AGORA</div>
            <div style={s.secaoDesc}>
              Use se você criou eventos/registros que <strong>aparecem aqui mas somem em outro navegador</strong>.
              Este botão força o envio do que está na sua tela pra nuvem, e mostra o erro real se falhar.
            </div>
          </div>
        </div>
        <button
          style={{ ...s.btnBaixar, background: '#f59e0b' }}
          onClick={forcarSincronizacao}
          disabled={sincronizandoForce}
        >
          <Cloud size={15}/> {sincronizandoForce ? 'Sincronizando...' : 'Forçar sincronização agora'}
        </button>
      </div>

      {/* Ações manuais */}
      <div className="form-grid-2" style={s.acoesRow}>
        <div style={s.card}>
          <div style={s.secaoHeader}>
            <Download size={18} color="#10b981"/>
            <div style={s.secaoTxt}>
              <div style={s.secaoTitulo}>Baixar backup agora</div>
              <div style={s.secaoDesc}>Gera e baixa um arquivo <code>.json</code> com todos os dados. Salve em local seguro.</div>
            </div>
          </div>
          <button style={s.btnBaixar} onClick={baixarBackup}>
            <Download size={15}/> Baixar Backup
          </button>
        </div>

        <div style={s.card}>
          <div style={s.secaoHeader}>
            <Upload size={18} color="#f59e0b"/>
            <div style={s.secaoTxt}>
              <div style={s.secaoTitulo}>Restaurar de arquivo</div>
              <div style={s.secaoDesc}>Importe um arquivo <code>.json</code>. <span style={{ color:'#f59e0b', fontWeight:600 }}>Os dados atuais serão substituídos.</span></div>
            </div>
          </div>
          <label style={s.btnRestaurar}>
            <Upload size={15}/> Escolher arquivo
            <input type="file" accept=".json" style={{ display:'none' }} onChange={restaurarArquivo}/>
          </label>
        </div>
      </div>

      {/* Configuração de email automático */}
      <div style={s.card}>
        <div style={s.secaoHeader}>
          <Mail size={20} color="#1d4ed8"/>
          <div style={s.secaoTxt}>
            <div style={s.secaoTitulo}>Email automático às 21h</div>
            <div style={s.secaoDesc}>
              O backup diário será enviado automaticamente para <strong style={{ color:'#1c1530' }}>bianca@kzeroconsultoria.com.br</strong>.
              Configure o serviço EmailJS gratuito para ativar.{' '}
              <a href="https://www.emailjs.com" target="_blank" rel="noreferrer" style={{ color:'#1d4ed8' }}>emailjs.com →</a>
            </div>
          </div>
        </div>

        {/* Toggle ativo */}
        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', userSelect:'none' }}>
          <div
            style={{ width:40, height:22, borderRadius:11, background: configEmail.ativo ? '#7c3aed' : '#ebe7f3', position:'relative', transition:'background 0.2s', flexShrink:0 }}
            onClick={() => setConfigEmail(c => ({ ...c, ativo: !c.ativo }))}
          >
            <div style={{ position:'absolute', top:3, left: configEmail.ativo ? 21 : 3, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.2s' }}/>
          </div>
          <span style={{ fontSize:13, color: configEmail.ativo ? '#5b21b6' : '#5f5874', fontWeight:600 }}>
            {configEmail.ativo ? 'Email automático ativado' : 'Email automático desativado'}
          </span>
        </label>

        <div className="form-grid-2" style={s.emailGrid}>
          <div style={s.campo}>
            <label style={s.label}>Service ID <span style={s.hint}>(EmailJS → Email Services)</span></label>
            <input style={s.input} type="text" placeholder="service_xxxxxxx" value={configEmail.serviceId}
              onChange={e => setConfigEmail(c => ({ ...c, serviceId: e.target.value }))} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Template ID <span style={s.hint}>(EmailJS → Email Templates)</span></label>
            <input style={s.input} type="text" placeholder="template_xxxxxxx" value={configEmail.templateId}
              onChange={e => setConfigEmail(c => ({ ...c, templateId: e.target.value }))} />
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Public Key <span style={s.hint}>(EmailJS → Account → General)</span></label>
            <div style={{ display:'flex', gap:6 }}>
              <input style={{ ...s.input, flex:1, WebkitTextSecurity: mostraSenha ? 'none' : 'disc' }} type="text" autoComplete="off" data-lpignore="true" placeholder="xxxxxxxxxxxxxxxxxxxxxx"
                value={configEmail.publicKey}
                onChange={e => setConfigEmail(c => ({ ...c, publicKey: e.target.value }))} />
              <button style={s.btnOlho} onClick={() => setMostraSenha(v => !v)}>
                {mostraSenha ? <EyeOff size={15}/> : <Eye size={15}/>}
              </button>
            </div>
          </div>
        </div>

        {/* Instruções */}
        <div style={s.instrucoes}>
          <div style={s.instrTitulo}><Settings size={13}/> Como configurar (passo a passo):</div>
          <ol style={s.instrLista}>
            <li>Acesse <strong>emailjs.com</strong> e crie uma conta gratuita</li>
            <li>Em <strong>Email Services</strong>, conecte seu Gmail ou outro servidor de email</li>
            <li>Em <strong>Email Templates</strong>, crie um template com as variáveis:<br/>
              <code style={s.code}>{'{{data_backup}}'}</code> <code style={s.code}>{'{{hora_backup}}'}</code> <code style={s.code}>{'{{estatisticas}}'}</code> <code style={s.code}>{'{{tamanho_arquivo}}'}</code> <code style={s.code}>{'{{conteudo_json}}'}</code>
            </li>
            <li>Defina o <strong>To Email</strong> do template como <code style={s.code}>bianca@kzeroconsultoria.com.br</code></li>
            <li>Cole os IDs acima e clique em Salvar</li>
          </ol>
        </div>

        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button style={s.btnSalvarEmail} onClick={salvarEmail}>
            <Settings size={14}/> Salvar configurações
          </button>
          <button style={s.btnTestar} onClick={testarEmail} disabled={enviandoTeste}>
            <Mail size={14}/> {enviandoTeste ? 'Enviando...' : 'Testar envio agora'}
          </button>
        </div>
      </div>

      {/* Histórico de versões na nuvem (Supabase Storage) */}
      <div style={s.card}>
        <div style={s.secaoHeader}>
          <History size={20} color="#10b981"/>
          <div style={s.secaoTxt}>
            <div style={s.secaoTitulo}>☁️ Histórico de versões — Nuvem (Supabase Storage)</div>
            <div style={s.secaoDesc}>
              Snapshot automático a cada 10 minutos no bucket <code style={s.code}>Backups</code>. Sobrevive a troca de navegador/máquina. Limite rotativo: últimas 500 versões.
            </div>
          </div>
        </div>
        <button style={s.btnAtualizar} onClick={carregarHistoricoNuvem} disabled={carregandoNuvem}>
          <RotateCcw size={13}/> {carregandoNuvem ? 'Carregando...' : 'Atualizar lista'}
        </button>
        {historicoNuvem.length === 0 ? (
          <div style={s.vazio}>
            <Clock size={32} color="#e5e0ef"/>
            <p>{carregandoNuvem ? 'Carregando...' : 'Nenhuma versão na nuvem ainda. O primeiro upload acontece no próximo ciclo de 10 minutos.'}</p>
          </div>
        ) : (
          <div style={s.listaHistorico}>
            {historicoNuvem.map(row => {
              const dt = new Date(row.criadoEm);
              const dataStr = dt.toLocaleDateString('pt-BR');
              const horaStr = dt.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
              const kb = (row.tamanho / 1024).toFixed(0);
              return (
                <div key={row.path} style={s.snapCard}>
                  <div style={s.snapInfo}>
                    <div style={s.snapTopo}>
                      <span style={{ ...s.snapTipo, color:'#10b981', borderColor:'rgba(16,185,129,0.3)', background:'rgba(16,185,129,0.1)' }}>☁️ Nuvem</span>
                      <span style={s.snapData}>{dataStr} às {horaStr}</span>
                    </div>
                    <span style={s.snapRegistros}>{kb} KB</span>
                  </div>
                  {confirmandoNuvem === row.path ? (
                    <div style={s.confirmarBox}>
                      <span style={{ fontSize:12, color:'#f59e0b' }}>⚠️ Dados atuais serão substituídos. Confirmar?</span>
                      <div style={{ display:'flex', gap:6 }}>
                        <button style={s.btnConfirmar} onClick={() => restaurarVersaoNuvem(row)}>
                          <CheckCircle size={13}/> Confirmar
                        </button>
                        <button style={s.btnCancelar} onClick={() => setConfirmandoNuvem(null)}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div style={s.snapAcoes}>
                      <button style={s.btnSnapDown} onClick={() => baixarSnapshotNuvemUI(row)} title="Baixar este backup">
                        <Download size={13}/>
                      </button>
                      <button style={s.btnSnapRestore} onClick={() => setConfirmandoNuvem(row.path)}>
                        <RotateCcw size={13}/> Restaurar
                      </button>
                      <button style={s.btnSnapDel} onClick={() => excluirSnapshotNuvemUI(row)} title="Excluir">
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Histórico de backups */}
      <div style={s.card}>
        <div style={s.secaoHeader}>
          <History size={20} color="#8b5cf6"/>
          <div style={s.secaoTxt}>
            <div style={s.secaoTitulo}>Histórico de backups</div>
            <div style={s.secaoDesc}>
              {historico.length} de 36 backups locais (~6h de cobertura).{' '}
              {slots > 0
                ? <span style={{ color:'#10b981' }}>Restam {slots} slot(s) disponíveis.</span>
                : <span style={{ color:'#f59e0b' }}>⚠️ Limite atingido — próximos backups removerão os mais antigos.</span>
              }
            </div>
          </div>
        </div>

        {historico.length === 0 ? (
          <div style={s.vazio}>
            <Clock size={32} color="#e5e0ef"/>
            <p>Nenhum backup salvo ainda. O primeiro será gerado automaticamente nos próximos 10 minutos.</p>
          </div>
        ) : (
          <div style={s.listaHistorico}>
            {historico.map(snap => (
              <div key={snap.id} style={s.snapCard}>
                <div style={s.snapInfo}>
                  <div style={s.snapTopo}>
                    <span style={{ ...s.snapTipo, ...(snap.tipo === 'automatico' ? s.snapTipoAuto : s.snapTipoManual) }}>
                      {snap.tipo === 'automatico' ? '🤖 Automático' : '👤 Manual'}
                    </span>
                    <span style={s.snapData}>{snap.data.split('-').reverse().join('/')} às {snap.hora}</span>
                  </div>
                  <span style={s.snapRegistros}>
                    {snap.totalRegistros} registros
                    {snap.dados && ` · ${(new Blob([JSON.stringify(snap.dados)]).size / 1024).toFixed(0)} KB`}
                  </span>
                </div>

                {confirmando === snap.id ? (
                  <div style={s.confirmarBox}>
                    <span style={{ fontSize:12, color:'#f59e0b' }}>⚠️ Dados atuais serão substituídos. Confirmar?</span>
                    <div style={{ display:'flex', gap:6 }}>
                      <button style={s.btnConfirmar} onClick={() => restaurarSnapshot(snap)}>
                        <CheckCircle size={13}/> Confirmar
                      </button>
                      <button style={s.btnCancelar} onClick={() => setConfirmando(null)}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div style={s.snapAcoes}>
                    <button style={s.btnSnapDown} onClick={() => baixarSnapshot(snap)} title="Baixar este backup">
                      <Download size={13}/>
                    </button>
                    <button style={s.btnSnapRestore} onClick={() => setConfirmando(snap.id)}>
                      <RotateCcw size={13}/> Restaurar
                    </button>
                    <button style={s.btnSnapDel} onClick={() => excluirSnapshot(snap.id)} title="Excluir">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  container:      { display:'flex', flexDirection:'column', gap:'1.5rem' },
  titulo:         { fontSize:'1.8rem', fontWeight:700, color:'#1c1530', margin:0 },
  alerta:         { borderRadius:10, padding:'12px 16px', fontSize:14, fontWeight:500 },
  alertaOk:       { background:'rgba(16,185,129,0.12)', border:'1px solid rgba(16,185,129,0.3)', color:'#10b981' },
  alertaErro:     { background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444' },
  alertaLimite:   { background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.4)', color:'#b45309', borderRadius:10, padding:'14px 16px', fontSize:13 },
  card:           { background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:12, padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1rem' },
  acoesRow:       { gap:'1rem' },
  secaoHeader:    { display:'flex', gap:'1rem', alignItems:'flex-start' },
  secaoTxt:       { display:'flex', flexDirection:'column', gap:3 },
  secaoTitulo:    { fontSize:15, fontWeight:700, color:'#1c1530' },
  secaoDesc:      { fontSize:13, color:'#5f5874', lineHeight:1.5 },
  statsGrid:      { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(100px,1fr))', gap:'0.75rem' },
  statBox:        { background:'#f7f5fb', borderRadius:10, padding:'1rem', textAlign:'center' },
  statNum:        { fontSize:'1.8rem', fontWeight:700 },
  statLabel:      { fontSize:12, color:'#5f5874', marginTop:4 },
  totalTxt:       { fontSize:12, color:'#5f5874' },
  btnBaixar:      { display:'flex', alignItems:'center', gap:8, background:'#10b981', color:'white', border:'none', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer', alignSelf:'flex-start' },
  btnRestaurar:   { display:'flex', alignItems:'center', gap:8, background:'transparent', color:'#f59e0b', border:'1.5px solid #f59e0b', borderRadius:10, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer', alignSelf:'flex-start' },

  // Histórico
  vazio:          { display:'flex', flexDirection:'column', alignItems:'center', gap:8, padding:'2rem', color:'#5f5874', fontSize:13, textAlign:'center' },
  listaHistorico: { display:'flex', flexDirection:'column', gap:'0.5rem', maxHeight:400, overflowY:'auto' },
  snapCard:       { display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f7f5fb', border:'1px solid #eeeaf5', borderRadius:10, padding:'10px 14px', gap:8, flexWrap:'wrap' },
  snapInfo:       { display:'flex', flexDirection:'column', gap:3 },
  snapTopo:       { display:'flex', alignItems:'center', gap:10 },
  snapTipo:       { fontSize:11, fontWeight:700, borderRadius:12, padding:'2px 8px', border:'1px solid' },
  snapTipoAuto:   { color:'#1d4ed8', borderColor:'rgba(96,165,250,0.3)', background:'rgba(96,165,250,0.1)' },
  snapTipoManual: { color:'#6d28d9', borderColor:'rgba(167,139,250,0.3)', background:'rgba(167,139,250,0.1)' },
  snapData:       { fontSize:13, color:'#1c1530', fontWeight:500 },
  snapRegistros:  { fontSize:11, color:'#5f5874' },
  snapAcoes:      { display:'flex', gap:6, alignItems:'center' },
  btnSnapDown:    { background:'transparent', border:'1px solid #e5e0ef', color:'#5f5874', borderRadius:8, padding:'5px 8px', cursor:'pointer', display:'flex', alignItems:'center' },
  btnSnapRestore: { display:'flex', alignItems:'center', gap:5, background:'rgba(124,58,237,0.15)', border:'1px solid rgba(124,58,237,0.4)', color:'#5b21b6', borderRadius:8, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer' },
  btnSnapDel:     { background:'transparent', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', borderRadius:8, padding:'5px 8px', cursor:'pointer', display:'flex', alignItems:'center' },
  btnAtualizar:   { display:'flex', alignItems:'center', gap:6, background:'transparent', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', padding:'6px 14px', fontSize:12, cursor:'pointer', alignSelf:'flex-start' },

  // Email config
  emailGrid:      { gap:'0.75rem' },
  campo:          { display:'flex', flexDirection:'column', gap:4 },
  label:          { fontSize:11, fontWeight:700, color:'#5f5874', textTransform:'uppercase', letterSpacing:'0.04em' },
  hint:           { fontSize:10, textTransform:'none', fontWeight:400, color:'#a09ab0' },
  btnOlho:        { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:8, color:'#5f5874', cursor:'pointer', padding:'0 10px', display:'flex', alignItems:'center' },
  instrucoes:     { background:'#ffffff', border:'1px solid #eeeaf5', borderRadius:10, padding:'12px 16px', display:'flex', flexDirection:'column', gap:8 },
  instrTitulo:    { display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700, color:'#5f5874' },
  instrLista:     { margin:0, paddingLeft:18, display:'flex', flexDirection:'column', gap:6, fontSize:12, color:'#5f5874', lineHeight:1.6 },
  code:           { background:'#f7f5fb', border:'1px solid #e5e0ef', borderRadius:4, padding:'1px 5px', fontSize:11, color:'#5b21b6', fontFamily:'monospace' },
  btnSalvarEmail: { display:'flex', alignItems:'center', gap:6, background:'#7c3aed', color:'white', border:'none', borderRadius:8, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },
  btnTestar:      { display:'flex', alignItems:'center', gap:6, background:'rgba(96,165,250,0.12)', color:'#1d4ed8', border:'1px solid rgba(96,165,250,0.3)', borderRadius:8, padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' },

  // Confirmação
  confirmarBox:   { display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' },
  btnConfirmar:   { display:'flex', alignItems:'center', gap:4, background:'#ef4444', color:'white', border:'none', borderRadius:8, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer' },
  btnCancelar:    { background:'transparent', border:'1px solid #e5e0ef', color:'#5f5874', borderRadius:8, padding:'5px 12px', fontSize:12, cursor:'pointer' },
};
